<template>
  <div
    class="flex flex-col"
    data-testid="form-text-area">
    <div class="items-top mb-2 flex justify-between">
      <label
        :for="id"
        data-testid="textarea-label"
        class="font-bold"
        :class="[disabled ? 'text-royal-blue-20' : 'text-royal-blue-160']"
      >
        {{ label }}
      </label>
      <span
        v-if="isOptional"
        data-testid="textarea-optional"
        :class="[disabled ? 'text-royal-blue-20' : 'text-royal-blue-160']"
      >
        optional
      </span>
    </div>
    <template v-if="helpText">
      <p
        :id="helpTextId"
        data-testid="textarea-help-text"
        class="mb-2"
        :class="[
          disabled
            ? 'cursor-not-allowed text-royal-blue-20'
            : 'text-royal-blue-160',
        ]"
      >
        {{ helpText }}
      </p>
    </template>
    <textarea
      v-bind="$attrs"
      :id="id"
      data-testid="textarea-field"
      :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value)"
      @change="$emit('custom-change', $event)"
      @focus="$emit('custom-focus', $event)"
      @blur="$emit('custom-blur', $event)"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="!isOptional"
      :rows="rows"
      class="block w-full appearance-none rounded-sm border-2 border-dark-grey bg-white px-4 py-2.5 placeholder-graphite focus:outline-hidden"
      :class="{
        'text-graphite hover:border-royal-blue-100 focus:border-royal-blue-160':
          !disabled && !readonly,
        'cursor-not-allowed border-royal-blue-20 text-royal-blue-20 placeholder-royal-blue-20 hover:border-royal-blue-20 focus:border-royal-blue-20':
          disabled || readonly,
        'border-ruby-red-100 hover:border-ruby-red-100 focus:border-ruby-red-100':
          errorMessage,
      }"
    />
    <template v-if="errorMessage">
      <div
        :id="errorId"
        class="items-top mt-4 flex space-x-2 bg-ruby-red-5 rounded p-4">
        <WarningTriangleIcon class="shrink-0 text-ruby-red-100" />
        <p data-testid="textarea-error-message">
          {{ errorMessage }}
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import WarningTriangleIcon from './icons/WarningTriangleIcon.vue'

/** Form textarea are for users to enter text more than a single line of text. */
export default {
  name: 'FormTextArea',
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      required: true,
    },
    /** Communicates the content the user needs to enter in the textarea. */
    label: {
      type: String,
      default: '',
    },
    /** Communicates to the user that entering content is optional for this textarea. */
    isOptional: {
      type: Boolean,
      default: false,
    },
    /** The content the user has entered into the textarea. */
    modelValue: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 3,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: null,
    },
    /** Provides additional feedback about what the user is required to do */
    helpText: {
      type: String,
      default: null,
    },
  },
  computed: {
    errorId() {
      return this.errorMessage ? `error-${this.id}` : null
    },
    helpTextId() {
      return this.helpText ? `help-${this.id}` : null
    },
    describedById() {
      return [this.errorId, this.helpTextId].filter(id => id).join(' ')
    },
  },
  components: {
    WarningTriangleIcon,
  },
  emits: [
    'update:modelValue',
    'custom-change',
    'custom-focus',
    'custom-blur',
  ], // custom-* events available for potential input validation/error message handling
}
</script>
